import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { getDeviceMonitorLatest } from "../device/deviceApi";
import { getOrganizationId } from "../manageData/reportsApi";
import { formatDeviceType } from "../../utils/deviceStatusHelpers";
import {
  applyMonitorReadings,
  formatSensorValue,
  getReadingColor,
  SENSOR_UNITS,
} from "../../pages/vehicle/vehicleTrack/liveTelemetry";
import { buildStopMonitorRows } from "../../utils/vehicleMonitorData";

const WORKING_CONDITION_LABELS = {
  WORKING: "Working",
  NOT_WORKING: "Not Working",
  NEEDS_REPAIR: "Needs Repair",
};

const WORKING_CONDITION_COLORS = {
  WORKING: "#1D9E75",
  NOT_WORKING: "#A32D2D",
  NEEDS_REPAIR: "#B7791F",
};

const POLL_MS = 15000;

const readingMatchesStop = (reading, stop) => {
  const ids = [stop?.id, stop?.stopId, stop?.stop_id].filter(Boolean).map(String);
  if (!ids.length) return false;
  return [
    reading?.stopId,
    reading?.stop_id,
    reading?.stop?.id,
    reading?.stop?.stopId,
  ]
    .filter(Boolean)
    .some((id) => ids.includes(String(id)));
};

const formatCondition = (device = {}) => {
  const condition = String(device.workingCondition || "").toUpperCase();
  if (WORKING_CONDITION_LABELS[condition]) {
    return {
      label: WORKING_CONDITION_LABELS[condition],
      color: WORKING_CONDITION_COLORS[condition],
    };
  }
  const status = String(device.status || "").toUpperCase();
  if (status === "ACTIVE" || status === "ONLINE" || status === "WORKING") {
    return { label: "Active", color: "#1D9E75" };
  }
  if (status) {
    return { label: status.replace(/_/g, " "), color: "#B7791F" };
  }
  return { label: "Assigned", color: "#183059" };
};

export function useStopConnectedDeviceCards(stop) {
  const organizationId = getOrganizationId();
  const [readings, setReadings] = useState([]);

  useEffect(() => {
    if (!stop?.id && !stop?.stopId) {
      setReadings([]);
      return undefined;
    }

    let cancelled = false;

    const loadReadings = async () => {
      try {
        const response = await getDeviceMonitorLatest({
          organizationId,
          stopId: stop.id || stop.stopId,
        });
        let allReadings = Array.isArray(response?.data) ? response.data : [];
        let matched = allReadings.filter((reading) =>
          readingMatchesStop(reading, stop),
        );
        if (!matched.length && !allReadings.length) {
          const orgResponse = await getDeviceMonitorLatest({ organizationId });
          allReadings = Array.isArray(orgResponse?.data) ? orgResponse.data : [];
          matched = allReadings.filter((reading) =>
            readingMatchesStop(reading, stop),
          );
        }
        if (!cancelled) setReadings(matched);
      } catch (error) {
        console.error("Error fetching stop connected devices:", error);
        if (!cancelled) setReadings([]);
      }
    };

    loadReadings();
    const intervalId = setInterval(loadReadings, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [organizationId, stop?.id, stop?.stopId]);

  const devices = useMemo(() => {
    const rows = buildStopMonitorRows([stop].filter(Boolean), readings);
    return rows[0]?.devices || [];
  }, [readings, stop]);

  return useMemo(() => {
    const telemetry = applyMonitorReadings(readings);
    const cards = [];
    const seen = new Set();

    devices.forEach((device) => {
      const type = device.type || device.sensorType || device.deviceType || "device";
      const key = `${type}-${device.id || device.trackingDeviceId}`;
      if (seen.has(key)) return;
      seen.add(key);

      const hasValue = Number.isFinite(Number(device.val));
      const condition = formatCondition(device);
      const unit = device.unit || SENSOR_UNITS[type] || "";
      cards.push({
        key,
        label: formatDeviceType(type),
        value: hasValue ? formatSensorValue(device.val, unit) : condition.label,
        hint: hasValue ? condition.label : "",
        color: hasValue
          ? getReadingColor(device.val, device.min, device.max)
          : condition.color,
      });
    });

    ["temperature", "humidity", "vibration", "smoke", "voltage"].forEach((type) => {
      if (telemetry[type] == null) return;
      if (cards.some((card) => card.label.toLowerCase() === type)) return;
      const range = telemetry.ranges?.[type] || {};
      const unit = range.unit || SENSOR_UNITS[type] || "";
      cards.push({
        key: `telemetry-${type}`,
        label: formatDeviceType(type),
        value: formatSensorValue(telemetry[type], unit),
        hint: "",
        color: getReadingColor(telemetry[type], range.min, range.max),
      });
    });

    return cards;
  }, [devices, readings]);
}

const headerDividerSx = {
  mx: { sm: 2 },
  my: { xs: 2, sm: 0 },
  height: { xs: "1px", sm: "60px" },
  width: { xs: "100%", sm: "auto" },
  alignSelf: "center",
  border: "1px solid #183059",
};

export default function StopHeaderDeviceItems({ stop, t }) {
  const cards = useStopConnectedDeviceCards(stop);
  if (!cards.length) return null;

  return cards.map((card) => (
    <React.Fragment key={card.key}>
      <Divider
        orientation={{ xs: "horizontal", sm: "vertical" }}
        flexItem
        sx={headerDividerSx}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 },
          minWidth: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: { xs: "16px", md: "20px" },
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
            color: "#183059",
            mb: 1,
          }}
        >
          {t(card.label)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: { xs: "18px", md: "24px" },
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
            color: card.color || "#050C1C",
          }}
        >
          {card.value}
        </Typography>
        {card.hint ? (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: { xs: "12px", md: "14px" },
              color: "#647490",
              textAlign: "center",
            }}
          >
            {t(card.hint)}
          </Typography>
        ) : null}
      </Box>
    </React.Fragment>
  ));
}
